import { useEffect, useRef, useState } from "react";

import { EditorContent } from "@tiptap/react";

import { styled } from "simplydo/react/styled";

import { useEditorContext } from "./EditorContext";

const InnerEditorWrapper = styled.div<{ $disableBorder: boolean }>`
  display: flex;

  .richtext-editor {
    cursor: text;
    box-sizing: border-box;
    position: relative;
    line-height: 1.6em;
    width: 100%;
    min-height: 25px !important;
    word-break: normal;
    overflow: hidden !important;
    overflow-wrap: anywhere;

    &:focus {
      outline: none;
    }

    &.ProseMirror-focused {
      .richtext-menubar {
        display: none;
      }
    }

    &[contenteditable="false"] {
      box-shadow: 0px 0px 0px 0 !important;
      padding: 0;
    }

    &[contenteditable="true"] {
      padding: 10px;
      border-radius: 5px;
      background-color: #fff;
      box-shadow: 0 0 0 ${({ $disableBorder }) => ($disableBorder ? 0 : 1)}px
        rgba(0, 0, 0, 0.1) !important;
      min-height: 90px !important;

      .ProseMirror-selectednode {
        outline: 1px dashed ${({ theme }) => theme?.primaryColour || "#0078d4"};
        border-radius: 1px;
      }
    }

    ul,
    ol {
      padding-block-start: 0px;
      padding-inline-start: 16px;
    }

    li {
      padding-left: 2px;
    }

    &[contenteditable="false"] {
      ul,
      ol {
        text-align: left;
      }
    }

    img {
      max-width: var(--parent-width, 100%) !important;
    }

    /* TipTap ResizableNodeView does not ship styles — handles are invisible without this. */
    .richtext-resize-container,
    .richtext-resize-wrapper {
      max-width: 100%;
      box-sizing: border-box;
    }

    .richtext-resize-wrapper {
      position: relative;
      display: block;
      max-width: 100%;
      line-height: 0;
    }

    /*
      TipTap's ResizableNodeView uses display:flex for block media (image/iframe), which
      ignores parent text-align. Force inline-flex so alignment wrappers can center/right
      them. Inline video already uses inline-flex from TipTap.
    */
    [data-resize-container] {
      display: inline-flex !important;
      max-width: 100%;
      vertical-align: middle;
    }

    img,
    iframe,
    .richtext-video-node {
      display: inline-block;
      max-width: 100%;
      vertical-align: middle;
    }

    .richtext-video-node {
      width: 100%;
      height: 100%;

      /* VideoPlayer caps height at 350px; clear that while the node has an explicit size. */
      .video-uploaded video,
      .video-embedded iframe,
      .can-expand .video-uploaded video,
      .can-expand .video-embedded iframe {
        max-height: none !important;
        width: 100% !important;
        height: 100% !important;
      }

      > div {
        width: 100%;
        height: 100%;
      }
    }

    [data-resize-handle] {
      position: absolute;
      z-index: 10;
      box-sizing: border-box;
      background: ${({ theme }) => theme.primaryColour || "#0078d4"};
      opacity: 0.75;
      border-radius: 2px;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
      /* Keep the webview from scrolling when a finger starts on a handle. */
      touch-action: none;
      -webkit-user-select: none;
      user-select: none;
    }

    [data-resize-container]:hover [data-resize-handle] {
      opacity: 1;
    }

    [data-resize-handle="top-left"],
    [data-resize-handle="top-right"],
    [data-resize-handle="bottom-left"],
    [data-resize-handle="bottom-right"] {
      width: 10px;
      height: 10px;
    }

    /*
      TipTap Image resize sets inline edge offsets (top:0 etc.) which would clip under
      overflow:hidden — force inset handles so they stay visible for images too.
    */
    [data-resize-handle="top-left"] {
      top: 4px !important;
      left: 4px !important;
      cursor: nwse-resize;
    }

    [data-resize-handle="top-right"] {
      top: 4px !important;
      right: 4px !important;
      cursor: nesw-resize;
    }

    [data-resize-handle="bottom-left"] {
      bottom: 4px !important;
      left: 4px !important;
      cursor: nesw-resize;
    }

    [data-resize-handle="bottom-right"] {
      bottom: 4px !important;
      right: 4px !important;
      cursor: nwse-resize;
    }

    [data-resize-handle="top"],
    [data-resize-handle="bottom"] {
      height: 6px;
      left: 8px !important;
      right: 8px !important;
      width: auto !important;
    }

    [data-resize-handle="top"] {
      top: 4px !important;
      cursor: ns-resize;
    }

    [data-resize-handle="bottom"] {
      bottom: 4px !important;
      cursor: ns-resize;
    }

    /* Full-height side bars (Image-style), not small corner squares. */
    [data-resize-handle="left"],
    [data-resize-handle="right"] {
      width: 4px !important;
      height: auto !important;
      top: 8px !important;
      bottom: 8px !important;
      border-radius: 999px;
    }

    [data-resize-handle="left"] {
      left: 4px !important;
      cursor: ew-resize;
    }

    [data-resize-handle="right"] {
      right: 4px !important;
      cursor: ew-resize;
    }

    /*
      Touch / stylus: slightly thicker bars + invisible hit padding so fingers can
      grab them. Fine pointers (desktop mouse) keep the slim web look above.
    */
    @media (pointer: coarse) {
      [data-resize-handle="left"],
      [data-resize-handle="right"] {
        width: 8px !important;
        top: 4px !important;
        bottom: 4px !important;
      }

      [data-resize-handle="left"]::before,
      [data-resize-handle="right"]::before {
        content: "";
        position: absolute;
        /* ~36px wide / taller target around the visible bar */
        inset: -12px -12px;
      }
    }

    &[contenteditable="false"] [data-resize-handle] {
      display: none !important;
    }

    .is-resizing,
    [data-resize-state="true"] {
      outline: 1px dashed ${({ theme }) => theme?.primaryColour || "#0078d4"};
      touch-action: none;

      [data-resize-handle] {
        opacity: 1;
      }
    }

    /*
      Iframes/videos steal pointer events mid-drag, so mouseup never reaches
      document and the resize feels sticky. Disable hit-testing while resizing.
    */
    .is-resizing iframe,
    .is-resizing video,
    .is-resizing .richtext-video-node,
    .is-resizing img,
    [data-resize-state="true"] iframe,
    [data-resize-state="true"] video,
    [data-resize-state="true"] .richtext-video-node,
    [data-resize-state="true"] img {
      pointer-events: none !important;
    }

    h1 {
      font-size: 1.5rem;
    }

    h2 {
      font-size: 1.25rem;
    }

    &[contenteditable="true"] table {
      .selectedCell:after {
        background: rgba(55, 55, 65, 0.05);
        content: "";
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        pointer-events: none;
        position: absolute;
        z-index: 2;
      }

      .column-resize-handle {
        background-color: ${({ theme }) => theme?.primaryColour || "#0078d4"};
        bottom: 0;
        pointer-events: none;
        position: absolute;
        right: -1px;
        top: -1px;
        width: 2px;
        z-index: 20;
      }
    }

    table {
      border-collapse: collapse;
      margin: 0;
      overflow: hidden;
      table-layout: fixed;
      width: 100%;

      td,
      th {
        border: 1px solid var(--gray-3, #dee2e6);
        box-sizing: border-box;
        min-width: 1em;
        padding: 6px 8px;
        position: relative;
        vertical-align: top;

        > * {
          margin-bottom: 0;
        }
      }

      th {
        background-color: var(--gray-1, #f8f9fa);
        font-weight: bold;
        text-align: left;
      }
    }

    .tableWrapper {
      margin: 1.5rem 0;
      overflow-x: auto;
    }

    &.resize-cursor {
      cursor: ew-resize;
      cursor: col-resize;
    }

    p {
      margin: 0;
    }

    a {
      text-decoration: none !important;
      color: ${({ theme }) => theme.primaryColour || "#0078d4"};
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
      color: #adadad;
      font-size: small !important;
      font-weight: normal !important;
    }

    .mention-suggest {
      pointer-events: none;
      cursor: default;
      color: ${({ theme }) => theme.primaryColour || "#0078d4"};
    }

    .mention,
    .mention-suggest {
      cursor: pointer;
      color: ${({ theme }) => theme.primaryColour || "#0078d4"};
    }

    s > .selection {
      text-decoration: line-through;
    }

    span[data-ext="file"] {
      cursor: pointer;
      background-color: ${({ theme }) =>
        theme.colors.primary.softer || "#0078d4"};
      padding: 2px 0;
      border-radius: 0.3rem;
      font-size: 0.9rem;
      vertical-align: center;
    }

    span[data-ext="file"] .file-node-view-spacer-left {
      margin-right: 2px;
    }

    span[data-ext="file"] .file-node-view-spacer-right {
      margin-left: 3px;
    }

    span[data-ext="file"] svg {
      margin-right: 4px;
      width: 11px;
      color: ${({ theme }) => theme.colors.primary.ink || "#000"};
    }

    .video-node-view-wrapper {
      display: inline-block;
    }

    span[data-node] {
      max-width: 98%;
      position: relative;
      min-width: 20px;
      min-height: 20px;
      display: inline-block;
      overflow: hidden;
      vertical-align: middle;

      &.mouseDown {
        > * {
          pointer-events: none;
        }
      }
      > * {
        object-fit: contain;
        width: 100%;
        height: 100%;
        display: inline-block;
      }
    }
  }
`;

type ReactTextEditorProps = { disableBorder?: boolean };

const ReactTextEditor = ({ disableBorder }: ReactTextEditorProps) => {
  const { editor } = useEditorContext();
  const sizingRef = useRef<HTMLDivElement>(null);
  const [parentWidth, setParentWidth] = useState(0);

  useEffect(() => {
    if (sizingRef.current) {
      const updateParentWidth = () => {
        const parent = sizingRef?.current?.parentElement;
        if (parent) {
          setParentWidth(parent.clientWidth - 20);
        }
      };
      updateParentWidth();

      const listener = () => {
        updateParentWidth();
      };
      window.addEventListener("resize", listener);
      return () => {
        window.removeEventListener("resize", listener);
      };
    }
  }, []);

  // Links are followed by the browser after our click handler runs, so we capture the click and
  // prevent the default to be able to show the external link warning first.
  // Use closest() so nested markup (e.g. <strong> inside <a>) still matches.
  useEffect(() => {
    const captureClicks = (e: MouseEvent) => {
      if (!(e.target instanceof Element)) return; // ignore non-elements such as simulated clicks
      if (e.target.closest(".ProseMirror.richtext-editor a[href]")) {
        e.preventDefault();
      }
    };

    window.addEventListener("click", captureClicks, true);
    return () => {
      window.removeEventListener("click", captureClicks, true);
    };
  }, []);

  // TipTap registers touchmove as passive, so preventDefault is ignored and the
  // mobile webview scrolls while dragging a handle. Block scroll for any active resize.
  useEffect(() => {
    const blockScrollWhileResizing = (event: TouchEvent) => {
      if (
        document.querySelector(".richtext-editor [data-resize-state='true']")
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener("touchmove", blockScrollWhileResizing, {
      passive: false,
    });
    return () => {
      document.removeEventListener("touchmove", blockScrollWhileResizing);
    };
  }, []);

  return (
    <>
      <InnerEditorWrapper
        $disableBorder={!!disableBorder}
        style={{ ["--parent-width" as string]: `${parentWidth}px` }}
      >
        <EditorContent editor={editor} style={{ flex: 1, minWidth: 0 }} />
      </InnerEditorWrapper>
      <div ref={sizingRef} />
    </>
  );
};

export default ReactTextEditor;
